
import React, { useState, useCallback } from "react";
import { TabsContent } from "@/components/ui/tabs";
import { Faq } from "@/types/faq-types";
import { PlusCircle, Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import FaqsTable from "@/components/admin/faqs/FaqsTable";
import FaqFormDialog from "@/components/admin/faqs/FaqFormDialog";

interface FaqsTabProps {
  faqs: Faq[];
  createFaq: (faq: any) => Promise<string | null>;
  updateFaq: (id: string, faq: any) => Promise<boolean>;
  deleteFaq: (id: string) => Promise<boolean>;
  uploadMedia: (file: File) => Promise<string | null>;
}

const FaqsTab = ({
  faqs,
  createFaq,
  updateFaq,
  deleteFaq,
  uploadMedia
}: FaqsTabProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedFaq, setSelectedFaq] = useState<Faq | null>(null);
  const { toast } = useToast();

  const handleAddNew = () => {
    setSelectedFaq(null);
    setFormOpen(true);
  };

  const handleEdit = (faq: Faq) => {
    setSelectedFaq(faq);
    setFormOpen(true);
  };

  const handleDelete = async (id: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      console.log("FaqsTab: Handling delete for FAQ ID:", id);
      const result = await deleteFaq(id);
      
      if (result) {
        toast({
          title: "FAQ deleted",
          description: "The FAQ has been successfully deleted."
        });
      } else {
        setError("Failed to delete FAQ");
      }
      
      return result;
    } catch (error: any) {
      console.error("Error deleting FAQ:", error);
      setError(error.message || "Failed to delete FAQ");
      toast({
        variant: "destructive",
        title: "Error deleting FAQ",
        description: error.message || "Failed to delete FAQ"
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormSubmit = async (formData: any) => {
    setIsLoading(true);
    setError(null);
    
    try {
      let result;
      if (selectedFaq) {
        result = await updateFaq(selectedFaq.id, formData);
        if (result) {
          toast({
            title: "FAQ updated",
            description: "The FAQ has been successfully updated."
          });
        }
      } else {
        result = await createFaq(formData);
        if (result) {
          toast({
            title: "FAQ created",
            description: "The FAQ has been successfully created."
          });
        }
      }
      
      if (result) {
        setFormOpen(false);
        return true;
      }
      return false;
    } catch (error: any) {
      console.error("Error saving FAQ:", error);
      setError(error.message || "Failed to save FAQ");
      toast({
        variant: "destructive",
        title: "Error saving FAQ",
        description: error.message || "Failed to save FAQ"
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const isFaqsLoading = faqs === undefined;

  return (
    <TabsContent value="faqs" className="space-y-4">
      <div className="flex justify-between items-center bg-corporate-primary-25 p-4 rounded-lg border border-corporate-primary-50">
        <h2 className="text-2xl font-bold text-corporate-primary-100">FAQs Management</h2>
        <Button 
          onClick={handleAddNew} 
          className="flex items-center gap-2 bg-corporate-primary-75 hover:bg-corporate-primary-100"
          disabled={isLoading}
        >
          {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlusCircle className="h-4 w-4" />}
          Add New FAQ
        </Button>
      </div>
      
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      {isFaqsLoading ? (
        <div className="text-center p-8 border rounded-lg flex flex-col items-center gap-4 bg-gray-50">
          <Loader2 className="h-8 w-8 animate-spin text-corporate-primary-100" />
          <p className="text-corporate-primary-75">Loading FAQs...</p>
        </div>
      ) : faqs.length === 0 ? (
        <div className="text-center p-8 border rounded-lg bg-gray-50">
          <p className="text-corporate-primary-75 mb-4">No FAQs found</p>
          <Button onClick={handleAddNew} variant="outline">
            <PlusCircle className="h-4 w-4 mr-2" />
            Create your first FAQ
          </Button>
        </div>
      ) : (
        <FaqsTable 
          faqs={faqs}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}

      {formOpen && (
        <FaqFormDialog
          open={formOpen}
          onOpenChange={setFormOpen}
          faq={selectedFaq}
          onSubmit={handleFormSubmit}
          isSubmitting={isLoading}
          uploadMedia={uploadMedia}
        />
      )}
    </TabsContent>
  );
};

export default FaqsTab;
